import { computed, onBeforeUnmount, onMounted, Ref, ref } from "@vue/composition-api";
import { differenceInMinutes, isAfter, isBefore } from "date-fns";
import { Notification } from "@scrile/api-provider/dist/api/NotificationsProvider";
import { Appointment, AppointmentStatus } from "@scrile/api-provider/dist/projects/scrileConnect/AppointmentProvider";
import { AppointmentTypeKind } from "@scrile/api-provider/dist/projects/scrileConnect/AppointmentTypeProvider";
import useAuthUser from "@/hooks/useAuthUser";
import providers from "@/lib/api";
import emitter from "@/lib/emitter";
import { $t } from "@/lib/i18nHelpers";
import { APPOINTMENT_ONLINE_REMIND_MS, EVENTS } from "@/const";

export default function useStartAppointment(opponentId: Ref<string>) {
  const { authUserStrict, isCreator } = useAuthUser();
  let timer: ReturnType<typeof setInterval> | null = null;
  const appointments = ref<Appointment[]>([]);
  const nearestAppointment = ref<Appointment | null>(null);

  const isAppointmentStart = ref(false);
  const isMessagingAppointmentStart = computed(
    () => isAppointmentStart.value && nearestAppointment.value?.typeKind === AppointmentTypeKind.MESSAGING
  );

  const getAppointments = async () => {
    const response = await providers.AppointmentProvider.search({
      ...(isCreator.value
        ? {
            providerId: authUserStrict.value.id,
            consumerId: opponentId.value,
          }
        : {
            providerId: opponentId.value,
            consumerId: authUserStrict.value.id,
          }),
      status: [AppointmentStatus.APPROVED],
      endTimeFrom: new Date().toISOString(),
      sort: "START_TIME,ASC",
    });
    appointments.value = response.result;
  };

  const removePastAppointments = () => {
    const filteredAppointments = appointments.value.filter(i => isAfter(new Date(i.endTime), new Date()));
    filteredAppointments.length < appointments.value.length &&
      emitter.emit(EVENTS.NOTIFICATION_SHOW, { text: $t("Appointment end") });
    appointments.value = filteredAppointments;
  };

  const setNearestAppointment = () => {
    if (
      appointments.value.length > 0 &&
      appointments.value[0].typeKind !== AppointmentTypeKind.TOUR_GUIDE &&
      differenceInMinutes(new Date(appointments.value[0].startTime), new Date()) <
        APPOINTMENT_ONLINE_REMIND_MS / 60 / 1000
    ) {
      nearestAppointment.value = appointments.value[0];
    } else {
      nearestAppointment.value = null;
    }
    isAppointmentStart.value =
      !!nearestAppointment.value && isAfter(new Date(), new Date(nearestAppointment.value.startTime));
  };

  const checkAppointment = () => {
    removePastAppointments();
    setNearestAppointment();
    timer = setTimeout(() => {
      checkAppointment();
    }, 1000);
  };

  const appointmentReminderHandler = (notification?: Notification) => {
    if (!notification) return;
    const startTime = new Date(notification.details.appointment.startTime);
    setTimeout(() => {
      if (!appointments.value.some(item => item.id === notification.details.appointmentId)) {
        appointments.value.push(notification.details.appointment);
        appointments.value = appointments.value.sort((i1, i2) =>
          isBefore(new Date(i1.startTime), new Date(i2.startTime)) ? -1 : 1
        );
      }
    }, Math.max(startTime.getTime() - APPOINTMENT_ONLINE_REMIND_MS - Date.now(), 0));
  };

  onMounted(async () => {
    await getAppointments();
    checkAppointment();
  });

  emitter.on<Notification>(EVENTS.UPCOMING_APPOINTMENT_REMINDER, appointmentReminderHandler);

  onBeforeUnmount(() => {
    timer && clearTimeout(timer);
    emitter.off<Notification>(EVENTS.UPCOMING_APPOINTMENT_REMINDER, appointmentReminderHandler);
  });

  return {
    nearestAppointment,
    isAppointmentStart,
    isMessagingAppointmentStart,
  };
}
